/**
 * Conflict Resolution Modal — shared between Agenda principal & Agenda Semanal (Colaboradores)
 * Shows when a drag-to-move action collides with an existing appointment and
 * offers two smart paths: (A) shift to next free slot with same pro
 * (B) switch to another available pro keeping the original time.
 */
const ConflictResolutionModal = ({ conflict, currentPro, onClose, onPickSlot, onPickPro }) => {
    if (!conflict) return null;
    const { requestedStartMs, conflictAppt, nextFreeMs, otherPros } = conflict;
    const reqDate = new Date(requestedStartMs);
    const reqLabel = `${reqDate.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "2-digit" })} · ${String(reqDate.getHours()).padStart(2, "0")}:${String(reqDate.getMinutes()).padStart(2, "0")}`;
    const nextLabel = nextFreeMs
        ? `${String(new Date(nextFreeMs).getHours()).padStart(2, "0")}:${String(new Date(nextFreeMs).getMinutes()).padStart(2, "0")}`
        : null;

    return (
        <div
            data-testid="conflict-modal"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="rounded-2xl border border-red-500/40 bg-slate-950 max-w-lg w-full p-6 space-y-5 shadow-[0_0_60px_-10px_rgba(239,68,68,0.5)]">
                <header className="flex items-start gap-3 pb-3 border-b border-red-500/20">
                    <div className="h-10 w-10 rounded-xl bg-red-500/20 border border-red-500/40 flex items-center justify-center text-red-300 flex-shrink-0">
                        <i className="fa-solid fa-triangle-exclamation"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="salon-heading text-xl text-white">Conflito de horário</h3>
                        <p className="text-xs text-slate-400 mt-1">
                            <span className="font-semibold text-amber-200">{currentPro?.name || "Colaborador"}</span> já tem
                            {" "}<span className="font-semibold text-white">{conflictAppt.clientName || conflictAppt.client}</span>
                            {" "}({conflictAppt.serviceName || conflictAppt.service}) nesse horário.
                        </p>
                        <p className="text-[11px] text-slate-500 mt-1.5 uppercase tracking-wider">
                            Solicitado: {reqLabel}
                        </p>
                    </div>
                    <button
                        data-testid="conflict-close"
                        onClick={onClose}
                        className="h-9 w-9 rounded-lg border border-white/10 hover:border-white/30 text-slate-400 hover:text-white transition-all flex-shrink-0"
                    >
                        <i className="fa-solid fa-xmark"></i>
                    </button>
                </header>

                <div className="space-y-3">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-bold">
                        Como você quer resolver?
                    </p>

                    {/* Option A — next free slot with same pro */}
                    {nextFreeMs ? (
                        <button
                            data-testid="conflict-pick-slot"
                            onClick={onPickSlot}
                            className="w-full text-left rounded-xl border border-amber-500/30 hover:border-amber-400 bg-amber-500/5 hover:bg-amber-500/10 p-4 transition-all group"
                        >
                            <div className="flex items-center gap-3">
                                <div className="h-10 w-10 rounded-lg bg-amber-500/15 border border-amber-500/40 flex items-center justify-center text-amber-300">
                                    <i className="fa-solid fa-clock-rotate-left"></i>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-bold text-white">
                                        Mover para o próximo horário livre
                                    </div>
                                    <div className="text-xs text-slate-400 mt-0.5">
                                        Com <span className="text-amber-200 font-semibold">{currentPro?.name}</span> às
                                        {" "}<span className="font-mono text-amber-200 font-bold">{nextLabel}</span> no mesmo dia
                                    </div>
                                </div>
                                <i className="fa-solid fa-arrow-right text-amber-300 group-hover:translate-x-0.5 transition-transform"></i>
                            </div>
                        </button>
                    ) : (
                        <div className="rounded-xl border border-white/5 bg-slate-900/40 p-4">
                            <div className="text-sm text-slate-400">
                                <i className="fa-solid fa-circle-info mr-2 text-slate-500"></i>
                                Não há mais slots livres com {currentPro?.name} nesse dia.
                            </div>
                        </div>
                    )}

                    {/* Option B — switch professional at the original requested time */}
                    {otherPros.length > 0 ? (
                        <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-4 space-y-3">
                            <div className="flex items-center gap-3">
                                <div className="h-10 w-10 rounded-lg bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center text-emerald-300">
                                    <i className="fa-solid fa-user-group"></i>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-bold text-white">
                                        Mover para outro colaborador
                                    </div>
                                    <div className="text-xs text-slate-400 mt-0.5">
                                        Mantém o horário <span className="font-mono text-emerald-200 font-bold">
                                            {String(reqDate.getHours()).padStart(2, "0")}:{String(reqDate.getMinutes()).padStart(2, "0")}
                                        </span> com um profissional disponível
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-1.5 pl-1">
                                {otherPros.map((p) => (
                                    <button
                                        key={p.id}
                                        data-testid={`conflict-pick-pro-${p.id}`}
                                        onClick={() => onPickPro(p)}
                                        className="w-full flex items-center gap-3 rounded-lg border border-emerald-500/20 hover:border-emerald-400 bg-slate-900/50 hover:bg-emerald-500/10 p-2.5 transition-all group text-left"
                                    >
                                        <img src={p.avatar} alt="" className="h-9 w-9 rounded-lg object-cover ring-1 ring-emerald-500/30" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-xs font-bold text-white truncate">{p.name}</div>
                                            <div className="text-[10px] text-slate-400 truncate">{p.role}</div>
                                        </div>
                                        <i className="fa-solid fa-arrow-right text-emerald-300 group-hover:translate-x-0.5 transition-transform"></i>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="rounded-xl border border-white/5 bg-slate-900/40 p-4">
                            <div className="text-sm text-slate-400">
                                <i className="fa-solid fa-circle-info mr-2 text-slate-500"></i>
                                Nenhum outro colaborador está livre nesse horário.
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex justify-end pt-2 border-t border-white/5">
                    <button
                        data-testid="conflict-cancel"
                        onClick={onClose}
                        className="min-h-[44px] px-5 rounded-xl border border-white/10 hover:border-white/30 text-slate-300 text-[11px] uppercase tracking-wider font-bold"
                    >
                        Cancelar
                    </button>
                </div>
            </div>
        </div>
    );
};

/**
 * findNextFreeSlot — returns the next 30-min-aligned start (ms) >= desiredStart
 * on the same calendar day where a slot of `durationMin` would NOT overlap any
 * of `dayApps` (filtered to the same calendar day). Returns null if no slot
 * is available before `dayEndMs`.
 */
export const findNextFreeSlot = (dayApps, desiredStartMs, durationMin, dayEndMs) => {
    const STEP = 30 * 60000;
    let cursor = desiredStartMs;
    const mod = cursor % STEP;
    if (mod !== 0) cursor = cursor + (STEP - mod);
    const durMs = durationMin * 60000;
    const sorted = [...dayApps].sort((a, b) => a.date - b.date);
    while (cursor + durMs <= dayEndMs) {
        const end = cursor + durMs;
        const hit = sorted.find((a) => {
            const aStart = a.date;
            const aEnd = a.estimated_end || aStart + (a.duration_min || 30) * 60000;
            return aStart < end && aEnd > cursor;
        });
        if (!hit) return cursor;
        cursor = hit.estimated_end || hit.date + (hit.duration_min || 30) * 60000;
        const m = cursor % STEP;
        if (m !== 0) cursor = cursor + (STEP - m);
    }
    return null;
};

export default ConflictResolutionModal;
